import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
} from 'shards-react';

import VChart from './VChart';

const rangeOptions = [
  { value: '7d', label: '1 Week' },
  { value: '30d', label: '1 Month' },
  { value: '12m', label: '1 Year' },
  { value: 'all', label: 'From Start' },
];

const chartOptions = {
  responsive: true,
  scales: {
    xAxes: [
      {
        gridLines: false,
        ticks: {
          autoSkip: true,
          maxRotation: 45,
          maxTicksLimit: 12,
          minRotation: 0,
        },
      },
    ],
    yAxes: [
      {
        ticks: {
          beginAtZero: true,
          precision: 0,
        },
      },
    ],
  },
};

const UsersOverview = ({
  loading,
  onRangeChange,
  range,
  title,
  users,
}) => {
  const { datasets, labels, total } = useMemo(() => {
    const sortedUsers = [...(Array.isArray(users) ? users : [])]
      .map(item => ({
        count: Number(item?.count || 0),
        period: item?.period || item?._id,
      }))
      .filter(item => item.period && Number.isFinite(item.count))
      .sort(
        (first, second) => String(first.period).localeCompare(String(second.period)),
      );
    const counts = sortedUsers.map(item => item.count);

    return {
      labels: sortedUsers.map(item => item.period),
      datasets: [{
        data: counts,
        fill: 'start',
        label: `Users (${counts.reduce((sum, count) => sum + count, 0).toLocaleString('en-US')})`,
      }],
      total: counts.reduce((sum, count) => sum + count, 0),
    };
  }, [users]);

  const buttonClassName = value => (
    `btn-white mx-1 my-1${range === value ? ' active' : ''}`
  );

  return (
    <Card small className="h-100">
      <CardHeader className="border-bottom">
        <h6 className="m-0">{title}</h6>
      </CardHeader>
      <CardBody className="pt-0">
        <Row className="border-bottom py-2 bg-light">
          <Col sm="12" className="d-flex flex-wrap justify-content-center">
            {rangeOptions.map(option => (
              <Button
                key={option.value}
                size="sm"
                disabled={loading}
                className={buttonClassName(option.value)}
                aria-pressed={range === option.value}
                onClick={() => onRangeChange(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </Col>
        </Row>
        {loading && labels.length === 0 ? (
          <div className="text-center text-muted p-4">
            Loading user data...
          </div>
        ) : labels.length > 0 && total > 0 ? (
          <VChart
            labels={labels}
            datasets={datasets}
            type="line"
            chartOptions={chartOptions}
          />
        ) : (
          <div className="text-center text-muted p-4">
            No user data in this range
          </div>
        )}
      </CardBody>
    </Card>
  );
};

UsersOverview.propTypes = {
  loading: PropTypes.bool,
  onRangeChange: PropTypes.func,
  range: PropTypes.string,
  title: PropTypes.string,
  users: PropTypes.array,
};

UsersOverview.defaultProps = {
  loading: false,
  onRangeChange: () => {},
  range: '30d',
  title: 'Users Overview',
  users: [],
};

export default UsersOverview;
